import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white border-t border-gray-200 text-gray-700">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-black text-2xl font-bold mb-4">AllAvail</h3>
            <p className="text-gray-600 leading-relaxed">
              {t('footer.companyDescription')}
            </p>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-black text-lg font-semibold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/terms-and-conditions"
                  className="hover:text-black transition-colors flex items-center text-gray-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('legal.termsAndConditions')}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy-policy"
                  className="hover:text-black transition-colors flex items-center text-gray-700"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {t('legal.privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="hover:text-black transition-colors flex items-center text-gray-700"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  FAQ
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Download App Banner */}
        <div className="border-t border-gray-200 mt-8 pt-8 mb-8">
          <div className="bg-black rounded-lg p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-2">{t('footer.downloadOurApp')}</h3>
            <p className="text-gray-300 mb-4">{t('footer.getBestExperience')}</p>
            <div className="flex justify-center gap-4">
              <a
                href={process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.allavail'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img
                  src="/play-store-badge.png"
                  alt={t('footer.getItOnGooglePlay')}
                  className="h-12 hover:opacity-80 transition-opacity"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      parent.innerHTML = `<span class="bg-white text-black px-4 py-2 rounded-lg font-semibold">${t('footer.googlePlay')}</span>`;
                    }
                  }}
                />
              </a>
              <a
                href={process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/app/allavail/id123456789'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img
                  src="/app-store-badge.png"
                  alt={t('footer.downloadOnAppStore')}
                  className="h-12 hover:opacity-80 transition-opacity"
                  onError={(e) => {
                    // Fallback if image doesn't exist
                    (e.target as HTMLImageElement).style.display = 'none';
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      parent.innerHTML = `<span class="bg-white text-black px-4 py-2 rounded-lg font-semibold">${t('footer.appStore')}</span>`;
                    }
                  }}
                />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-600">
              © {currentYear} AllAvail. {t('footer.copyright')}
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Shield className="h-4 w-4 text-gray-600 mr-2" />
              <p className="text-sm text-gray-600">
                {t('footer.securePlatform')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

